import { Box } from '@cowsquare/design';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { graphql, Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { GET_HKDU_USER_INFO, GET_USER_INFO } from '../../../../apollo/graphql/user';
import { getDoctorId } from '../../../../utils/doctorUtils';
import { getQueryObject } from '../../../../utils/getQueryObject';
import { useAdsQuery, useFilterTags, usePagesBindingUrl } from '../../../../utils/hooks';
import { Advertisements } from '../../../Advertisements';
import { EventCardList } from '../../../EventCardList';
import { FilterTags } from '../../../FilterTags';
import { EventCardListLoading, TagListLoading } from '../../../Loading';
import { NoResult } from '../../../NoResult';
import { Section } from '../../../Section';
import { Title } from '../../../Title';

const PAGE_SIZE = 21;

const GET_MEMBER_ONLY_HKDU_EVENTS = gql`
  query getMemberOnlyHKDUEvents(
    $limit: Int
    $offset: Int
    $doctor_id: uuid!
    $where: hkdutest_hkdu_events_bool_exp
  ) {
    tags: hkdutest_hkdu_event_tags {
      name
    }

    total: hkdutest_hkdu_events_aggregate(where: $where) {
      aggregate {
        count
      }
    }

    list: hkdutest_hkdu_events(limit: $limit, order_by: { date: desc }, offset: $offset, where: $where) {
      id
      name
      location
      start_time
      end_time
      content
      created_at
      date
      price
      member_price
      doctors(where: { doctor_id: { _eq: $doctor_id } }) {
        id
      }
      tags {
        tag {
          name
        }
      }
    }
  }
`;

const getCardOptions = () => ({ height: '225px' });

const EventList = ({ location, uri, HKDUUserInfo }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const [tag, handleTagChange] = useFilterTags(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const whereClause = R.equals('All', tag) ? null : { tags: { tag: { name: { _in: [tag] } } } };
  const doctorId = getDoctorId(HKDUUserInfo);

  return (
    <Query
      query={GET_MEMBER_ONLY_HKDU_EVENTS}
      skip={!doctorId}
      variables={{
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        doctor_id: doctorId,
        where: whereClause,
      }}
    >
      {({ data, loading }) => {
        const list = R.propOr([], 'list', data);

        const tags = R.pipe(
          R.propOr([], 'tags'),
          R.pluck('name'),
          R.unless(R.isEmpty, R.prepend('All'))
        )(data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);
        return (
          <Section>
            <Helmet>
              <title>HKDU Events - HKDU Members Only</title>
            </Helmet>
            <Advertisements ads={ads} />
            <Box px={[5, 0]} py={8}>
              <Box mb={5}>
                <Title m={0} mb={[5, 0]}>
                  HKDU Events
                </Title>
              </Box>
              <Box mb={6}>
                {R.and(R.isEmpty(tags), loading) && <TagListLoading />}
                <FilterTags value={tag} tags={tags} onChange={handleTagChange} />
              </Box>
              <Box pb={8}>
                {R.and(R.isEmpty(list), !loading) && <NoResult>No result found...</NoResult>}
                {R.and(R.isEmpty(list), loading) && <EventCardListLoading />}
                <EventCardList
                  uri={uri}
                  page={page}
                  onPageChange={onPageChange}
                  list={list}
                  total={total}
                  options={getCardOptions}
                />
              </Box>
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default R.compose(
  graphql(GET_USER_INFO, { name: 'userInfo' }),
  graphql(GET_HKDU_USER_INFO, {
    name: 'HKDUUserInfo',
    options: ({ userInfo }) => ({
      variables: {
        id: R.pathOr(null, ['user', 'id'], userInfo),
      },
    }),
  })
)(EventList);
